import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AddContactInformationService } from "../../../../core/services/add-contact-information.service";
import { ActivatedRoute, Params } from "@angular/router";
import { AddressCountryRegion } from "../../../../core/models/client/addclient/countryRegionList";
import { Zip } from "../../../../core/models/client/addclient/zipList";
import { District } from "../../../../core/models/client/addclient/districtList";
import { AddressCounty } from "../../../../core/models/client/addclient/countyList";
import { AddressState } from "../../../../core/models/client/addclient/stateList";
import { LoaderService } from "../../../../core/services/loader.service";
import { City } from "../../../../core/models/client/addclient/cityList";
import { ClientPurpose } from "../../../../shared/enums/clientPurpose";
import {
  AddCustomerAddressRequestDto,
  AddressDto,
} from "../../../../core/models/client/details/addCustomerAddressRequestDto";
import { CommonModule } from "@angular/common";
import { DrawerModule } from "primeng/drawer";
import { AddressComponent } from "../../../../shared/components/address/address.component";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { AddNewClientService } from "../../../../core/services/add-new-client.service";
import { Address } from "../../../../core/models/shared/address";
import { ButtonModule } from "primeng/button";

@UntilDestroy()
@Component({
  selector: "jni-add-address-information",
  templateUrl: "./add-address-information.component.html",
  imports: [CommonModule, DrawerModule, AddressComponent, ButtonModule],
  styleUrls: ["./add-address-information.component.scss"],
})
export class AddAddressInformationComponent implements OnInit {
  clientForm: FormGroup;

  @Input() display!: boolean;
  @Output() displayChange = new EventEmitter<boolean>();
  @Input() headingStatus = true;
  @Input() addressObject!: Address;
  @Input() isPrimaryFieldStatus = true;
  @Output() refreshSummary = new EventEmitter<boolean>();

  countryRegionSelectedId = "USA";
  isPrimaryOne = "1";
  isPrimaryZero = "0";
  createNewAddress = "Create New";
  updateNewAddress = "Edit Address Information";
  stateSelectedId = "";
  clientId = "";
  zipSelectionCode = "";
  countryRegionList: AddressCountryRegion[] = [];
  zipList: Zip[] = [];
  districtList: District[] = [];
  cityList: City[] = [];
  countyList: AddressCounty[] = [];
  stateList: AddressState[] = [];
  addressValidationStatus = false;
  showSmallLoader = false;

  postingDate: Date = new Date();

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input("clientIdFromModal")
  set clientIdFromModal(value: string) {
    this.clientId = value;
  }

  purposeTypes = [
    { name: "Business", value: 1 },
    { name: "Invoice", value: 2 },
    { name: "Payment", value: 3 },
    { name: "Delivery", value: 4 },
    { name: "Other", value: 5 },
    { name: "Buyer", value: 6 },
  ];

  constructor(
    private _toastrService: JniToastrService,
    private _formBuilder: FormBuilder,
    private router: ActivatedRoute,
    private _contactInformationService: AddContactInformationService,
    private _addNewClientService: AddNewClientService,
    private _loaderService: LoaderService
  ) {
    this.clientForm = this._formBuilder.group({
      addressInformation: this._formBuilder.group({
        countryRegionIdD365: ["", [Validators.required]],
        zipCodeD365: [""],
        districtNameD365: [""],
        postBoxD365: [""],
        cityD365: [""],
        countyD365: [""],
        streetNumberD365: [""],
        stateD365: [""],
        addressD365: [""],
        streetD365: [""],
        notes: [""],
        purposeD365: ClientPurpose.Business,
        isPrimaryD365: [false],
        addressLocationNameD365: [""],
        buildingComplimentD365: [""],
        apartment_RUD365: [""],
        addressFormat: [""],
        validateAddress: [""],
        addressLocationNumberD365: [0],
        melissaAddressKeyD365: [""],
        addressLine1: [""],
        addressLine2: [""],
        addressLine3: [""],
      }),
    });
    this.router.params.subscribe((params: Params) => {
      this.clientId = params["id"];
    });
  }

  ngOnInit(): void {
    if (this.addressObject && this.headingStatus !== true) {
      if (Object.keys(this.addressObject).length !== 0) {
        this.patchValueInAddressForm(this.addressObject);
        this.headingStatus = false;
        this.clientForm.get("type")?.disable();
      }
    } else {
      this.clientForm.get("type")?.enable();
    }
    this.getCountryRegionListData();
  }

  getCountryRegionListData() {
    this._addNewClientService
      .getCountryRegionList()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.countryRegionList = res?.data;
        },
      });
  }

  patchValueInAddressForm(addressObject: Address): void {
    setTimeout(() => {
      this.clientForm.patchValue({
        addressInformation: {
          countryRegionIdD365: addressObject.countryRegionIdD365,
          zipCodeD365: addressObject.zipCodeD365,
          districtNameD365: addressObject.districtNameD365,
          postBoxD365: addressObject.postBoxD365,
          cityD365: addressObject.cityD365,
          countyD365: addressObject.countyD365,
          streetNumberD365: addressObject.streetNumberD365,
          stateD365: addressObject.stateD365,
          addressD365: addressObject.addressD365,
          streetD365: addressObject.streetD365,
          addressLocationNameD365:
            addressObject?.addressLocationNameD365 ??
            addressObject?.descriptionD365,
          buildingComplimentD365: addressObject.buildingComplimentD365,
          apartment_RUD365: addressObject.apartment_RUD365,
          isPrimaryD365:
            typeof addressObject.isPrimaryD365 === "string"
              ? (addressObject.isPrimaryD365 as string)
                  .toLowerCase()
                  .includes("yes")
              : Boolean(addressObject.isPrimaryD365),
          addressLocationNumberD365: parseInt(
            addressObject.locationNumberD365 ?? "0"
          ),
          purposeD365: addressObject?.rolesD365
            ? this.purposeTypes.find(x => x.name === addressObject?.rolesD365)
                ?.value
            : 1,
        },
      });
    }, 0);

    this.countryRegionSelectedId = addressObject.countryRegionIdD365 ?? "";
  }

  onHide() {
    this.displayChange.emit(false);
  }

  convertAddressControllerName(addObject: {
    addressLocationNumberD365: number;
    addressLocationNameD365: string;
    cityD365: string;
    streetD365: string;
    streetNumberD365: string;
    countryRegionIdD365: string;
    stateD365: string;
    zipCodeD365: string;
    countyD365: string;
    buildingComplimentD365: string;
    districtNameD365: string;
    postBoxD365: string;
    melissaAddressKeyD365: string;
    isPrimaryD365: boolean | string;
    purposeD365: string;
  }): AddressDto {
    const addressObject: AddressDto = {
      LocationNumber: addObject.addressLocationNumberD365.toString(),
      AddressLocationName: addObject.addressLocationNameD365.toString(),
      City: addObject.cityD365.toString(),
      Street: addObject.streetD365.toString(),
      StreetNumber: addObject.streetNumberD365.toString(),
      CountryRegionID: addObject.countryRegionIdD365.toString(),
      State: addObject.stateD365.toString(),
      ZipCode: addObject.zipCodeD365.toString(),
      County: addObject.countyD365.toString(),
      BuildingComplement: addObject.buildingComplimentD365.toString(),
      DistrictName: addObject.districtNameD365.toString(),
      PostBox: addObject.postBoxD365.toString(),
      melissaAddressKeyD365: addObject.melissaAddressKeyD365,
      IsPrimary:
        addObject.isPrimaryD365 === true ||
        (typeof addObject.isPrimaryD365 == "string" &&
          addObject.isPrimaryD365.toLowerCase() == "yes")
          ? 1
          : 0,
      Purpose: parseInt(addObject.purposeD365),
      Message: "",
      Status: "",
      StatusCode: 0,
    };
    return addressObject;
  }

  onSubmit() {
    const addAddressDto = this.clientForm.getRawValue();
    this._loaderService.setLoaderState(true);

    if (addAddressDto.addressInformation.isPrimaryD365) {
      addAddressDto.addressInformation.isPrimaryD365 = "YES";
    } else {
      addAddressDto.addressInformation.isPrimaryD365 = "NO";
    }

    const convertedAddressObject = this.convertAddressControllerName(
      addAddressDto.addressInformation
    );

    const addressArray = [];
    addressArray.push(convertedAddressObject);

    const address: AddCustomerAddressRequestDto = {
      AccountNumber: this.clientId,
      Addresses: addressArray,
    };

    if (this.headingStatus) {
      this._contactInformationService
        .addAddressInformationData(address)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.status.toLowerCase() !== "success") {
              this._toastrService.error(
                "Error",
                res.message || "Error in adding address"
              );
            } else {
              this._toastrService.success(
                "Success",
                res.message || "Address added successfully"
              );
            }
            this.displayChange.emit(false);
            this.refreshSummary.emit();
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastrService.error(
              "Error",
              res.message || "Error in adding address"
            );
          },
        });
    } else {
      this._contactInformationService
        .updateAddressInformationData(address)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.status.toLowerCase() !== "success") {
              this._toastrService.error(
                "Error",
                res.message || "Error in updating address"
              );
            } else {
              this._toastrService.success(
                "Success",
                res.message || "Address update successfully"
              );
            }
            this.displayChange.emit(false);
            this.refreshSummary.emit();
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastrService.error(
              "Error",
              res.message || "Error in updating address"
            );
          },
        });
    }
  }
}
